var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { EventEmitter } from 'eventemitter3';
import io from 'socket.io-client';
import { v4 as uuid } from 'uuid';
class RPChatsAPIv1 extends EventEmitter {
    constructor(config) {
        super();
        this.config = config;
        this._CONNECT_TIMEOUT_MS = 15 * 1000;
        this._DISCONNECT_TIMEOUT_MS = 15 * 1000;
        this._onError = (error) => {
            this.emit('error', error);
        };
        this._onDisconnect = (error) => {
            // https://socket.io/docs/v3/client-api/index.html#Event-%E2%80%98disconnect%E2%80%99
            const reconnectableErrors = ["io server disconnect"];
            if (reconnectableErrors.find(_ => _ === error)) {
                return this._socket.connect();
            }
        };
    }
    init() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this._socket) {
                throw new Error('Already initialized');
            }
            this._socket = io(this._generateConnectionURL(), { autoConnect: false, query: { token: this.config.token, user_id: this.config.userId + "" }, transports: this.config.transports || ['polling', 'websocket'], rejectUnauthorized: false });
            this._socket.on('internal-error', (error, params) => this.emit('internal-error', error, params));
            this._socket.on('chats', (chats, params) => this.emit('chats', chats, params));
            this._socket.on('chats/describe', (chat, params) => this.emit('chats/describe', chat, params));
            this._socket.on('chats/members', (members, chatId, params) => this.emit('chats/members', members, chatId, params));
            this._socket.on('chats/new', (chat, params) => this.emit('chats/new', chat, params));
            this._socket.on('chats/edit', (chat, params) => this.emit('chats/edit', chat, params));
            this._socket.on('chats/delete', (chat, params) => this.emit('chats/delete', chat, params));
            this._socket.on('channels', (channels, params) => this.emit('channels', channels, params));
            this._socket.on('channels/describe', (channel, params) => this.emit('channels/describe', channel, params));
            this._socket.on('channels/members', (members, channelId, params) => this.emit('channels/members', members, channelId, params));
            this._socket.on('channels/new', (channel, params) => this.emit('channels/new', channel, params));
            this._socket.on('channels/edit', (channel, params) => this.emit('channels/edit', channel, params));
            this._socket.on('channels/delete', (channel, params) => this.emit('channels/delete', channel, params));
            this._socket.on('channels/read', (response, params) => this.emit('channels/read', response, params));
            this._socket.on('channels/unread', (response, params) => this.emit('channels/unread', response, params));
            this._socket.on('channels/leave', (channel, userId, params) => this.emit('channels/leave', channel, userId, params));
            this._socket.on('messages', (messages, page, params) => this.emit('messages', messages, page, params));
            this._socket.on('messages/new', (message, params) => this.emit('messages/new', message, params));
            this._socket.on('messages/edit', (message, params) => this.emit('messages/edit', message, params));
            this._socket.on('messages/delete', (message, params) => this.emit('messages/delete', message, params));
            return new Promise((resolve, reject) => {
                const timeout = setTimeout(() => {
                    this._socket.off();
                    this._socket = null;
                    return reject(new Error('Connection timeout'));
                }, this._CONNECT_TIMEOUT_MS);
                this._socket.once('initialized', () => {
                    this._socket.off('connect_error');
                    this._socket.off('error');
                    this._socket.on('connect_error', this._onError);
                    this._socket.on('error', this._onError);
                    this._socket.on('disconnect', this._onDisconnect);
                    clearTimeout(timeout);
                    return resolve();
                });
                this._socket.once('connect_error', (error) => {
                    this._socket.off();
                    this._socket = null;
                    clearTimeout(timeout);
                    return reject(error);
                });
                this._socket.once('error', (error) => {
                    this._socket.off();
                    this._socket = null;
                    clearTimeout(timeout);
                    return reject(error);
                });
                this._socket.connect();
            });
        });
    }
    deinit() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this._socket) {
                throw new Error('Already deinitialized');
            }
            return new Promise((resolve, reject) => {
                const timeout = setTimeout(() => {
                    this._socket.off();
                    this._socket = null;
                    return reject(new Error('Disconnection timeout'));
                }, this._DISCONNECT_TIMEOUT_MS);
                this._socket.off('connect_error', this._onError);
                this._socket.off('error', this._onError);
                this._socket.off('disconnect', this._onDisconnect);
                this._socket.once('disconnect', () => {
                    this._socket.off();
                    this._socket = null;
                    clearTimeout(timeout);
                    return resolve();
                });
                this._socket.once('error', (error) => {
                    this._socket.off();
                    this._socket = null;
                    clearTimeout(timeout);
                    return reject(error);
                });
                this._socket.disconnect();
            });
        });
    }
    chats() {
        const messageId = uuid();
        console.log('chats', messageId, this._socket.id, this._socket.connected);
        this._socket.emit('chats', { messageId });
        return messageId;
    }
    chatsDescribe(chatId) {
        const messageId = uuid();
        this._socket.emit('chats/describe', chatId, { messageId });
        return messageId;
    }
    chatsMembers(chatId) {
        const messageId = uuid();
        this._socket.emit('chats/members', chatId, { messageId });
        return messageId;
    }
    chatsMembersEdit(chatId, membersToAdd, membersToRemove) {
        const messageId = uuid();
        this._socket.emit('chats/members/edit', chatId, membersToAdd, membersToRemove, { messageId });
        return messageId;
    }
    chatsNew(newChat) {
        const messageId = uuid();
        this._socket.emit('chats/new', newChat, { messageId });
        return messageId;
    }
    chatsEdit(editChat) {
        const messageId = uuid();
        this._socket.emit('chats/edit', editChat, { messageId });
        return messageId;
    }
    chatsDelete(deleteChat) {
        const messageId = uuid();
        this._socket.emit('chats/delete', deleteChat, { messageId });
        return messageId;
    }
    channels() {
        const messageId = uuid();
        this._socket.emit('channels', { messageId });
        return messageId;
    }
    channelsDescribe(channelId) {
        const messageId = uuid();
        this._socket.emit('channels/describe', channelId, { messageId });
        return messageId;
    }
    channelsMembers(channelId) {
        const messageId = uuid();
        this._socket.emit('channels/members', channelId, { messageId });
        return messageId;
    }
    channelsMembersEdit(channelId, membersToAdd, membersToRemove) {
        const messageId = uuid();
        this._socket.emit('channels/members/edit', channelId, membersToAdd, membersToRemove, { messageId });
        return messageId;
    }
    channelsNew(newChannel) {
        const messageId = uuid();
        this._socket.emit('channels/new', newChannel, { messageId });
        return messageId;
    }
    channelsEdit(editChannel) {
        const messageId = uuid();
        this._socket.emit('channels/edit', editChannel, { messageId });
        return messageId;
    }
    channelsDelete(deleteChannel) {
        const messageId = uuid();
        this._socket.emit('channels/delete', deleteChannel, { messageId });
        return messageId;
    }
    channelsRead(request) {
        const messageId = uuid();
        this._socket.emit('channels/read', request, { messageId });
        return messageId;
    }
    channelsUnread() {
        const messageId = uuid();
        this._socket.emit('channels/unread', { messageId });
        return messageId;
    }
    channelsLeave(request) {
        const messageId = uuid();
        this._socket.emit('channels/leave', request, { messageId });
        return messageId;
    }
    messages(request) {
        const messageId = uuid();
        this._socket.emit('messages', request, { messageId });
        return messageId;
    }
    messagesNew(newMessage) {
        const messageId = uuid();
        this._socket.emit('messages/new', newMessage, { messageId });
        return messageId;
    }
    messagesEdit(editMessage) {
        const messageId = uuid();
        this._socket.emit('messages/edit', editMessage, { messageId });
        return messageId;
    }
    messagesDelete(deleteMessage) {
        const messageId = uuid();
        this._socket.emit('messages/delete', deleteMessage, { messageId });
        return messageId;
    }
    _generateConnectionURL() {
        return `${this.config.host}:${this.config.port}/${this.config.namespace}`;
    }
}
export { RPChatsAPIv1 };
